import { useId, useState } from 'react';
import { STAGE_LABEL, STAGE_ORDER, orderAssignees, type Stage } from '@/domain';
import { useStageAssignees } from '@/application/usecases/useBulk';
import { useAssigneeOrder } from '@/application/usecases/useAssigneeOrder';
import { AssigneeLine, LeastBusyToggle } from '@/ui/data/AssigneeLine';
import { strings } from '@/shared/strings';
import { SkeletonRows } from '@/ui/feedback/Loading';
import { ConfirmSheet } from '@/ui/primitives/ConfirmSheet';
import { modalStyles } from '@/ui/primitives/Modal';
import people from '@/features/queue/AssignDialog.module.css';
import styles from './BulkAssignDialog.module.css';

/**
 * Every department a selection may be sent to. Never Ready, which is what
 * passing Final Check means: a list that could skip the gate would make the
 * gate optional. The API refuses it in the same words.
 */
const DESTINATIONS = STAGE_ORDER.filter((stage) => stage !== 'ready');

export interface BulkAssignChoice {
  /** Send them here, or leave them where they are. */
  stage: Stage | null;
  /** Give them to this person at the department they end up at, or to nobody in particular. */
  userId: string | null;
}

export interface BulkAssignDialogProps {
  count: number;
  /** The one department every case in the selection is at, or null when they are at several. */
  commonStage: Stage | null;
  busy: boolean;
  /** A refusal of the whole request, shown where the choice was made. */
  error: string | null;
  onCancel: () => void;
  onConfirm: (choice: BulkAssignChoice) => void;
}

/**
 * Assigning a selection to a department, a person, or both. Section 4 of the
 * request of 24 September 2026: "assign all to a particular department or
 * person".
 *
 * The department first, because a person is given the cases at the department
 * they end up at, and who works there is the server's answer, not this screen's.
 * Leaving them where they are is the first choice, so giving a bench's trays to
 * one person there is two taps; and when the cases sit at several departments
 * the people wait until one is chosen, since nobody works all of them but
 * management. The single assign's person buttons, so the two pickers read alike.
 *
 * Since the request of 5 October 2026 (third), section 2, the dialog is the
 * confirmation and not a form in front of one: built on the shared
 * `ConfirmSheet`, as the single assign is, with a question that follows the
 * department and the person as they are chosen. Until something is chosen
 * there is nothing to confirm, and the button waits.
 */
export function BulkAssignDialog({
  count,
  commonStage,
  busy,
  error,
  onCancel,
  onConfirm,
}: BulkAssignDialogProps) {
  const departmentId = useId();
  const [stage, setStage] = useState<Stage | null>(null);
  const [userId, setUserId] = useState<string | null>(null);

  // Where the cases will be, which is who may be given them.
  const destination = stage ?? commonStage;
  const candidates = useStageAssignees(destination);
  // Least busy first, on request. Section 7 of the request of 5 October 2026.
  const { order, leastBusy, setLeastBusy } = useAssigneeOrder();

  const nothing = stage === null && userId === null;
  const chosenName = candidates.data?.find((person) => person.id === userId)?.name ?? null;

  return (
    <ConfirmSheet
      title={`${strings.bulk.assignTitle} ${count} ${count === 1 ? strings.bulk.one : strings.bulk.many}`}
      statement={strings.confirm.assignMany(
        count,
        stage === null ? null : STAGE_LABEL[stage],
        chosenName,
      )}
      confirmLabel={strings.bulk.assign}
      cancelLabel={strings.bulk.cancel}
      closeLabel={strings.sheet.close}
      onConfirm={() => onConfirm({ stage, userId })}
      onCancel={onCancel}
      busy={busy}
      confirmDisabled={nothing}
    >
      <div className={modalStyles.field}>
        <span className={modalStyles.hint}>{strings.bulk.assignLead}</span>
      </div>

      <div className={modalStyles.field}>
        <label className={modalStyles.label} htmlFor={departmentId}>
          {strings.bulk.department}
        </label>
        <select
          id={departmentId}
          className={modalStyles.select}
          value={stage ?? ''}
          data-autofocus
          onChange={(event) => {
            setStage(event.target.value === '' ? null : (event.target.value as Stage));
            // Somebody chosen at one bench may not work the next.
            setUserId(null);
          }}
        >
          <option value="">
            {commonStage === null
              ? strings.bulk.whereTheyAre
              : `${strings.bulk.whereTheyAre} · ${STAGE_LABEL[commonStage]}`}
          </option>
          {DESTINATIONS.map((key) => (
            <option key={key} value={key}>
              {STAGE_LABEL[key]}
            </option>
          ))}
        </select>
      </div>

      <div className={modalStyles.field}>
        <span className={modalStyles.label}>{strings.bulk.person}</span>

        {destination === null && <p className={styles.hint}>{strings.bulk.pickDepartmentFirst}</p>}
        {destination !== null && candidates.isPending && (
          <SkeletonRows rows={3} label={strings.bulk.loadingPeople} />
        )}
        {destination !== null && candidates.isError && (
          <p className={modalStyles.error}>{strings.errors.server}</p>
        )}

        {destination !== null && candidates.data !== undefined && candidates.data.length > 1 && (
          <LeastBusyToggle on={leastBusy} onChange={setLeastBusy} />
        )}

        {destination !== null && candidates.data !== undefined && (
          <div className={people.people} role="group" aria-label={strings.bulk.person}>
            <button
              type="button"
              className={`${people.person} ${userId === null ? people.on : ''}`}
              aria-pressed={userId === null}
              onClick={() => setUserId(null)}
            >
              <span className={people.name}>{strings.bulk.nobody}</span>
            </button>
            {orderAssignees(candidates.data, order).map((person) => (
              <button
                key={person.id}
                type="button"
                className={`${people.person} ${userId === person.id ? people.on : ''}`}
                aria-pressed={userId === person.id}
                onClick={() => setUserId(person.id)}
              >
                <AssigneeLine
                  name={person.name}
                  department={person.department}
                  activeCases={person.activeCases}
                />
              </button>
            ))}
            {candidates.data.length === 0 && <p className={styles.hint}>{strings.bulk.nobodyThere}</p>}
          </div>
        )}
      </div>

      {nothing && <p className={styles.hint}>{strings.bulk.chooseSomething}</p>}
      {error !== null && (
        <p className={modalStyles.error} role="alert">
          {error}
        </p>
      )}
    </ConfirmSheet>
  );
}
